import { CirclePlusIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { NavLink } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"

/**
 * The main navigation — `dashboard-01`'s `NavMain`, with the links actually
 * attached.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BLOCK'S ITEMS DO NOT NAVIGATE. It declares `url: string` on every item and
 * then renders `<SidebarMenuButton>` with only an icon and a label inside — the
 * URL is accepted and dropped. In a screenshot that is invisible; in a running
 * app every nav item is a button that highlights on hover and does nothing.
 *
 * `render={<NavLink to={...} />}` is how shadcn's Sidebar composes: the button
 * hands its styling to the element it is given, so the anchor is real, the
 * middle-click and open-in-new-tab work, and the router owns the transition.
 *
 * The active state comes from `NavLink`'s own `isActive` rather than from a
 * string comparison this file would have to keep true. `end` on Home, or `"/"`
 * would prefix-match every route and stay lit forever.
 *
 * THE INBOX BUTTON IS GONE. The block puts a mail icon beside Quick Create;
 * in-app email is §17A and is not built — stage 1 is not even started, and it
 * depends on SPF/DKIM/DMARC being configured first. A button that opens nothing
 * is the same defect as the block's social-login buttons on the sign-in screen.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export interface NavItem {
  title: string
  url: string
  icon?: React.ReactNode
  /** Match this path exactly — for Home, which would otherwise always match. */
  end?: boolean
}

export function NavMain({
  items,
  quickCreate,
}: {
  items: NavItem[]
  /**
   * The create actions, already capability-filtered by the caller. Empty for a
   * role that may not create anything, in which case the button is not rendered
   * at all rather than opening an empty menu.
   */
  quickCreate: { title: string; url: string }[]
}) {
  const { t } = useTranslation()

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        {quickCreate.length === 0 ? null : (
          <SidebarMenu>
            <SidebarMenuItem className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <SidebarMenuButton
                      tooltip={t("btn.quickCreate")}
                      className="min-w-8 bg-primary text-primary-foreground duration-200 ease-linear hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground"
                    />
                  }
                >
                  <CirclePlusIcon />
                  <span>{t("btn.quickCreate")}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-48">
                  {quickCreate.map((action) => (
                    <DropdownMenuItem
                      key={action.url}
                      render={<NavLink to={action.url} />}
                    >
                      {action.title}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        <SidebarMenu>
          {items.map((item) => (
            <SidebarMenuItem key={item.url}>
              <NavLink to={item.url} end={item.end ?? false}>
                {({ isActive }) => (
                  <SidebarMenuButton tooltip={item.title} isActive={isActive}>
                    {item.icon}
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
